
import { useState, useEffect } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { supabase } from "@/integrations/supabase/client";
import { Helmet } from 'react-helmet';

export const HomeSeo = () => {
  const [faqs, setFaqs] = useState([]);
  const [openFaqs, setOpenFaqs] = useState(new Set());
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    fetchFaqs();
  }, []);

  const fetchFaqs = async () => {
    try {
      const { data, error } = await supabase
        .from('faq_items')
        .select(`
          *,
          seo_pages (
            slug,
            title
          )
        `)
        .eq('published', true)
        .order('order_index');

      if (error) throw error;
      setFaqs(data || []);
    } catch (error) {
      console.error('Error fetching FAQs:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const toggleFaq = (id: string) => {
    const newOpenFaqs = new Set(openFaqs);
    if (newOpenFaqs.has(id)) {
      newOpenFaqs.delete(id);
    } else {
      newOpenFaqs.add(id);
    }
    setOpenFaqs(newOpenFaqs);
  };

  if (isLoading || faqs.length === 0) return null;
  
  // Prepare FAQ schema for structured data
  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqs.map(faq => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.answer
      }
    }))
  };

  return (
    <section className="py-16 bg-secondary/10">
      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify(faqSchema)}
        </script>
      </Helmet>
      
      <div className="container mx-auto px-4 md:px-6">
        <h2 className="text-2xl md:text-3xl font-bold mb-8 text-center">
          Frequently Asked Questions About Mobile Plans
        </h2>
        
        <div className="max-w-3xl mx-auto space-y-4">
          {faqs.map((faq) => (
            <div
              key={faq.id}
              className="bg-card rounded-lg shadow-sm overflow-hidden"
            >
              <button
                onClick={() => toggleFaq(faq.id)}
                className="w-full text-left px-6 py-4 flex justify-between items-center hover:bg-secondary/5 transition-colors"
                aria-expanded={openFaqs.has(faq.id)}
              >
                <span className="font-medium">{faq.question}</span>
                {openFaqs.has(faq.id) ? (
                  <ChevronUp className="h-5 w-5 flex-shrink-0" />
                ) : (
                  <ChevronDown className="h-5 w-5 flex-shrink-0" />
                )}
              </button>
              
              {openFaqs.has(faq.id) && (
                <div className="px-6 py-4 bg-background border-t">
                  <div className="prose max-w-none" dangerouslySetInnerHTML={{ __html: faq.answer }} />
                  
                  {faq.seo_pages && (
                    <div className="mt-4">
                      <a
                        href={`/${faq.seo_pages.slug}`}
                        className="text-primary hover:underline"
                      >
                        Read more about {faq.seo_pages.title}
                      </a>
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};
